import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ShoppingBag, Plus, X, Trash2, Upload, Image, Video, 
  ChevronLeft, ChevronRight, Edit, Eye, EyeOff, Star, Lock,
  ArrowUp, ArrowDown, Grid, LayoutList
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebshopPage = () => {
  const { isMember, isAdmin, getToken } = useAuth();
  const navigate = useNavigate();
  const getAuthHeaders = () => ({ Authorization: `Bearer ${getToken()}` });
  
  // Redirect if not a member
  if (!isMember) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="mc-card p-8 text-center max-w-md">
          <Lock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-['Oswald'] font-bold text-white mb-4">
            KUN FOR MEDLEMMER
          </h1>
          <p className="text-gray-400 mb-6">
            Webshoppen er kun tilgængelig for klubbens medlemmer.
          </p>
          <Link to="/login" className="radial-btn px-6 py-3 rounded-lg inline-block">
            Log ind med medlemskode
          </Link>
        </div>
      </div>
    );
  }

  return <WebshopContent isAdmin={isAdmin} />;
};

const WebshopContent = ({ isAdmin }) => {
  const { getToken } = useAuth();
  const getAuthHeaders = () => ({ Authorization: `Bearer ${getToken()}` });
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [gridCols, setGridCols] = useState(2); // 1 or 2 columns
  
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    short_description: "",
    price: "",
    category: "",
    sizes: [],
    is_active: true
  });
  const [savingProduct, setSavingProduct] = useState(false);
  const [savedProduct, setSavedProduct] = useState(false);

  // Size presets for clothing
  const sizePresets = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];

  useEffect(() => {
    fetchProducts();
  }, [showInactive]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/webshop/products?include_inactive=${showInactive}`);
      setProducts(res.data);
    } catch (err) {
      console.error("Kunne ikke hente produkter");
    } finally {
      setLoading(false);
    }
  };

  const moveProduct = async (productId, direction) => {
    const idx = products.findIndex(p => p.product_id === productId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === products.length - 1)) return;
    const newOrder = [...products];
    const [item] = newOrder.splice(idx, 1);
    newOrder.splice(idx + direction, 0, item);
    setProducts(newOrder);
    try {
      await axios.put(`${API}/webshop/products/reorder`, { product_ids: newOrder.map(p => p.product_id) }, { headers: getAuthHeaders() });
    } catch (err) {
      toast.error("Kunne ikke ændre rækkefølge");
      fetchProducts();
    }
  };


  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setSavingProduct(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        sizes: formData.sizes.length > 0 ? formData.sizes : null
      };
      
      if (editingProduct) {
        await axios.put(`${API}/webshop/products/${editingProduct.product_id}`, payload, { headers: getAuthHeaders() });
        toast.success("Produkt opdateret");
      } else {
        await axios.post(`${API}/webshop/products`, payload, { headers: getAuthHeaders() });
        toast.success("Produkt oprettet");
      }
      
      setSavedProduct(true);
      setTimeout(() => { setSavedProduct(false); resetForm(); }, 1200);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke gemme produkt");
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Er du sikker på du vil slette dette produkt?")) return;
    try {
      await axios.delete(`${API}/webshop/products/${productId}`, { headers: getAuthHeaders() });
      toast.success("Produkt slettet");
      setSelectedProduct(null);
      fetchProducts();
    } catch (err) {
      toast.error("Kunne ikke slette produkt");
    }
  };

  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const handleUploadImage = async (e, productId) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        await axios.post(`${API}/webshop/products/${productId}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() }
        });
      } catch (err) {
        toast.error(`Kunne ikke uploade ${file.name}`);
      }
    }
    
    toast.success("Billeder uploadet");
    setUploadingImages(false);
    fetchProducts();
    
    // Refresh selected product
    if (selectedProduct?.product_id === productId) {
      const res = await axios.get(`${API}/webshop/products/${productId}`);
      setSelectedProduct(res.data);
    }
    
    e.target.value = '';
  };

  const handleUploadVideo = async (e, productId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingVideo(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/webshop/products/${productId}/videos`, formData, {
        headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() }
      });
      toast.success("Video uploadet");
      fetchProducts();
      
      if (selectedProduct?.product_id === productId) {
        const res = await axios.get(`${API}/webshop/products/${productId}`);
        setSelectedProduct(res.data);
      }
    } catch (err) {
      toast.error("Kunne ikke uploade video");
    } finally {
      setUploadingVideo(false);
    }
    
    e.target.value = '';
  };

  const handleDeleteImage = async (productId, imageId) => {
    try {
      await axios.delete(`${API}/webshop/products/${productId}/images/${imageId}`, { headers: getAuthHeaders() });
      toast.success("Billede slettet");
      fetchProducts();
      
      if (selectedProduct?.product_id === productId) {
        const res = await axios.get(`${API}/webshop/products/${productId}`);
        setSelectedProduct(res.data);
        setCurrentImageIndex(0);
      }
    } catch (err) {
      toast.error("Kunne ikke slette billede");
    }
  };

  const handleDeleteVideo = async (productId, videoId) => {
    try {
      await axios.delete(`${API}/webshop/products/${productId}/videos/${videoId}`, { headers: getAuthHeaders() });
      toast.success("Video slettet");
      fetchProducts();
      
      if (selectedProduct?.product_id === productId) {
        const res = await axios.get(`${API}/webshop/products/${productId}`);
        setSelectedProduct(res.data);
      }
    } catch (err) {
      toast.error("Kunne ikke slette video");
    }
  };

  const handleSetPrimaryImage = async (productId, imageId) => {
    try {
      await axios.put(`${API}/webshop/products/${productId}/images/${imageId}/primary`, {}, { headers: getAuthHeaders() });
      toast.success("Primært billede opdateret");
      fetchProducts();
      
      if (selectedProduct?.product_id === productId) {
        const res = await axios.get(`${API}/webshop/products/${productId}`);
        setSelectedProduct(res.data);
      }
    } catch (err) {
      toast.error("Kunne ikke opdatere primært billede");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      short_description: "",
      price: "",
      category: "",
      sizes: [],
      is_active: true
    });
    setShowCreateForm(false);
    setEditingProduct(null);
  };

  const openEditForm = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description,
      short_description: product.short_description || "",
      price: product.price.toString(),
      category: product.category || "",
      sizes: product.sizes || [],
      is_active: product.is_active
    });
    setShowCreateForm(true);
  };

  const toggleSize = (size) => {
    const existing = formData.sizes.find(s => s.size === size);
    if (existing) {
      setFormData(prev => ({
        ...prev,
        sizes: prev.sizes.filter(s => s.size !== size)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        sizes: [...prev.sizes, { size, stock: 0, price_adjustment: 0 }]
      }));
    }
  };

  const getProductImage = (product) => {
    if (!product.images || product.images.length === 0) {
      return null;
    }
    const primary = product.images.find(img => img.is_primary);
    return primary || product.images[0];
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(price);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="webshop-page">
      {/* Hero Section */}
      <div className="relative h-[40vh] min-h-[300px]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black" />
        
        <div className="absolute bottom-16 left-0 right-0 px-6 sm:px-12">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-5xl sm:text-6xl font-['Oswald'] font-bold text-white drop-shadow-lg mb-4">
              WEBSHOP
            </h1>
            <p className="text-xl text-gray-200 drop-shadow-lg max-w-xl">
              The Others MC merchandise og klubudstyr
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-black px-6 sm:px-12 py-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Admin Controls */}
          {isAdmin && (
            <div className="flex flex-wrap gap-4 mb-8">
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateForm(true);
                }}
                className="chrome-btn px-6 py-3 rounded-lg flex items-center gap-2"
                data-testid="create-product-btn"
              >
                <Plus size={20} />
                Tilføj produkt
              </button>
              
              <button
                onClick={() => setShowInactive(!showInactive)}
                className={`ghost-blue-btn px-4 py-3 rounded-lg flex items-center gap-2 ${showInactive ? 'bg-blue-600/20' : ''}`}
              >
                {showInactive ? <Eye size={20} /> : <EyeOff size={20} />}
                {showInactive ? "Vis kun aktive" : "Vis alle"}
              </button>
            </div>
          )}

          {/* View Toggle */}
          <div className="flex justify-end mb-6 gap-2">
            <button
              onClick={() => setGridCols(1)}
              className={`p-2.5 rounded-lg border transition-all ${gridCols === 1 ? 'bg-white/10 border-white/30 text-white' : 'border-white/10 text-gray-500 hover:text-white'}`}
              data-testid="grid-1-col-btn"
              title="1 produkt per række"
            >
              <LayoutList size={20} />
            </button>
            <button
              onClick={() => setGridCols(2)}
              className={`p-2.5 rounded-lg border transition-all ${gridCols === 2 ? 'bg-white/10 border-white/30 text-white' : 'border-white/10 text-gray-500 hover:text-white'}`}
              data-testid="grid-2-col-btn"
              title="2 produkter per række"
            >
              <Grid size={20} />
            </button>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : products.length === 0 ? (
            <div className="mc-card p-12 text-center">
              <ShoppingBag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Ingen produkter endnu</p>
              {isAdmin && (
                <p className="text-gray-500 mt-2">Klik "Tilføj produkt" for at oprette dit første produkt</p>
              )}
            </div>
          ) : (
            <div className={`grid gap-6 ${gridCols === 1 ? 'grid-cols-1 max-w-2xl mx-auto' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
              {products.map((product, idx) => {
                const primaryImage = getProductImage(product);
                const hasVideo = product.videos && product.videos.length > 0;
                const firstVideo = hasVideo ? product.videos[0] : null;
                return (
                  <div
                    key={product.product_id}
                    className={`group relative overflow-hidden rounded-xl bg-neutral-900 border transition-all ${
                      product.is_active ? 'border-neutral-800 hover:border-blue-500/50' : 'border-red-900/50 opacity-60'
                    }`}
                    data-testid={`product-card-${product.product_id}`}
                  >
                    {/* Product Media */}
                    <div className={`${gridCols === 1 ? 'aspect-video' : 'aspect-square'} bg-neutral-800 overflow-hidden cursor-pointer`}
                      onClick={() => { setSelectedProduct(product); setCurrentImageIndex(0); }}
                    >
                      {hasVideo ? (
                        <video
                          src={firstVideo.url ? `${BACKEND_URL}${firstVideo.url}` : `${BACKEND_URL}/api/uploads/products/${firstVideo.filename}`}
                          autoPlay muted loop playsInline
                          className="w-full h-full object-cover"
                        />
                      ) : primaryImage ? (
                        <img
                          src={primaryImage.url ? `${BACKEND_URL}${primaryImage.url}` : `${BACKEND_URL}/api/uploads/products/${primaryImage.filename}`}
                          alt={product.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-16 h-16 text-neutral-600" />
                        </div>
                      )}
                    </div>
                    
                    {/* Product Info */}
                    <div className="p-4 cursor-pointer" onClick={() => { setSelectedProduct(product); setCurrentImageIndex(0); }}>
                      <h3 className="font-['Oswald'] text-lg font-bold text-white mb-1 line-clamp-1">
                        {product.title}
                      </h3>
                      <p className="text-gray-400 text-sm line-clamp-2 mb-3">
                        {product.short_description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--neon-blue)] font-bold text-lg">
                          {formatPrice(product.price)}
                        </span>
                        {product.sizes && product.sizes.length > 0 && (
                          <span className="text-gray-500 text-sm">
                            {product.sizes.length} størrelser
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Inactive badge */}
                    {!product.is_active && (
                      <div className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                        Inaktiv
                      </div>
                    )}

                    {/* Admin: Reorder arrows */}
                    {isAdmin && (
                      <>
                        <div className="absolute top-2 left-2 flex gap-1 z-10">
                          <button
                            onClick={(e) => { e.stopPropagation(); moveProduct(product.product_id, -1); }}
                            disabled={idx === 0}
                            className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:opacity-40 text-white px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1 text-xs font-bold shadow-lg"
                            data-testid={`move-up-${product.product_id}`}
                          >
                            <ArrowUp size={14} /> Op
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); moveProduct(product.product_id, 1); }}
                            disabled={idx === products.length - 1}
                            className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:opacity-40 text-white px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1 text-xs font-bold shadow-lg"
                            data-testid={`move-down-${product.product_id}`}
                          >
                            <ArrowDown size={14} /> Ned
                          </button>
                        </div>
                        {product.is_active && (
                          <div className="absolute top-2 right-2 flex gap-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${(product.images?.length || 0) >= 6 ? 'bg-green-600/80 text-green-100' : 'bg-yellow-600/80 text-yellow-100'}`}>
                              {product.images?.length || 0} img
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${(product.videos?.length || 0) >= 1 ? 'bg-green-600/80 text-green-100' : 'bg-yellow-600/80 text-yellow-100'}`}>
                              {product.videos?.length || 0} vid
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedProduct(null)}
        >
          <div 
            className="mc-card max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Close button */}
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white p-2"
              >
                <X size={24} />
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Image Gallery */}
                <div>
                  {/* Main Image */}
                  <div className="aspect-square bg-neutral-800 rounded-xl overflow-hidden mb-4 relative">
                    {selectedProduct.images && selectedProduct.images.length > 0 ? (
                      <>
                        <img
                          src={selectedProduct.images[currentImageIndex]?.url ? `${BACKEND_URL}${selectedProduct.images[currentImageIndex].url}` : `${BACKEND_URL}/api/uploads/products/${selectedProduct.images[currentImageIndex]?.filename}`}
                          alt={selectedProduct.title}
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Navigation arrows */}
                        {selectedProduct.images.length > 1 && (
                          <>
                            <button
                              onClick={() => setCurrentImageIndex(prev => 
                                prev === 0 ? selectedProduct.images.length - 1 : prev - 1
                              )}
                              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-2 rounded-full"
                            >
                              <ChevronLeft size={24} />
                            </button>
                            <button
                              onClick={() => setCurrentImageIndex(prev => 
                                prev === selectedProduct.images.length - 1 ? 0 : prev + 1
                              )}
                              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-2 rounded-full"
                            >
                              <ChevronRight size={24} />
                            </button>
                          </>
                        )}

                        {/* Admin: Delete image */}
                        {isAdmin && (
                          <div className="absolute top-2 right-2 flex gap-2">
                            <button
                              onClick={() => handleSetPrimaryImage(selectedProduct.product_id, selectedProduct.images[currentImageIndex].image_id)}
                              className={`p-2 rounded-full ${selectedProduct.images[currentImageIndex]?.is_primary ? 'bg-yellow-500' : 'bg-black/50 hover:bg-yellow-500/50'}`}
                              title="Sæt som primært billede"
                            >
                              <Star size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteImage(selectedProduct.product_id, selectedProduct.images[currentImageIndex].image_id)}
                              className="bg-red-600/80 hover:bg-red-600 p-2 rounded-full"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-24 h-24 text-neutral-600" />
                      </div>
                    )}
                  </div>

                  {/* Thumbnails */}
                  {selectedProduct.images && selectedProduct.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedProduct.images.map((img, idx) => (
                        <button
                          key={img.image_id}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                            idx === currentImageIndex ? 'border-[var(--neon-blue)]' : 'border-transparent opacity-60 hover:opacity-100'
                          }`}
                        >
                          <img
                            src={img.url ? `${BACKEND_URL}${img.url}` : `${BACKEND_URL}/api/uploads/products/${img.filename}`}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Videos */}
                  {selectedProduct.videos && selectedProduct.videos.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Videoer</h4>
                      {selectedProduct.videos.map((video) => (
                        <div key={video.video_id} className="relative rounded-lg overflow-hidden mb-2">
                          <video
                            src={video.url ? `${BACKEND_URL}${video.url}` : `${BACKEND_URL}/api/uploads/products/${video.filename}`}
                            autoPlay muted loop playsInline
                            className="w-full rounded-lg"
                          />
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteVideo(selectedProduct.product_id, video.video_id)}
                              className="absolute top-2 right-2 bg-red-600/80 hover:bg-red-600 p-2 rounded-full"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Admin: Upload buttons */}
                  {isAdmin && (
                    <div className="mt-4 space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Billeder</span>
                          <span className={`text-xs font-mono ${(selectedProduct.images?.length || 0) >= 6 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {selectedProduct.images?.length || 0} / 6 min.
                          </span>
                        </div>
                        <div className="w-full bg-neutral-800 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all ${(selectedProduct.images?.length || 0) >= 6 ? 'bg-green-500' : 'bg-yellow-500'}`}
                            style={{ width: `${Math.min(100, ((selectedProduct.images?.length || 0) / 6) * 100)}%` }}
                          />
                        </div>
                        {(selectedProduct.images?.length || 0) < 6 && (
                          <p className="text-yellow-400/80 text-xs">
                            Upload mindst {6 - (selectedProduct.images?.length || 0)} billede{6 - (selectedProduct.images?.length || 0) !== 1 ? 'r' : ''} mere
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Videoer</span>
                          <span className={`text-xs font-mono ${(selectedProduct.videos?.length || 0) >= 1 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {selectedProduct.videos?.length || 0} / 1 min.
                          </span>
                        </div>
                        <div className="w-full bg-neutral-800 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all ${(selectedProduct.videos?.length || 0) >= 1 ? 'bg-green-500' : 'bg-yellow-500'}`}
                            style={{ width: `${Math.min(100, ((selectedProduct.videos?.length || 0) / 1) * 100)}%` }}
                          />
                        </div>
                        {(selectedProduct.videos?.length || 0) < 1 && (
                          <p className="text-yellow-400/80 text-xs">
                            Upload mindst 1 video
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,.heic,.heif,.webp,.avif,.bmp,.tiff,.svg"
                          multiple
                          onChange={(e) => handleUploadImage(e, selectedProduct.product_id)}
                          className="hidden"
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImages}
                          className="ghost-blue-btn px-4 py-2 rounded-lg flex items-center gap-2 text-sm flex-1 disabled:opacity-50"
                          data-testid="upload-images-btn"
                        >
                          {uploadingImages ? <><span className="w-4 h-4 border-2 border-[#00d4ff]/30 border-t-[#00d4ff] rounded-full animate-spin" />Uploader...</> : <><Image size={16} />Tilføj billeder</>}
                        </button>
                        
                        <input
                          ref={videoInputRef}
                          type="file"
                          accept="video/*,.mp4,.mov,.avi,.webm,.mkv,.m4v"
                          onChange={(e) => handleUploadVideo(e, selectedProduct.product_id)}
                          className="hidden"
                        />
                        <button
                          onClick={() => videoInputRef.current?.click()}
                          disabled={uploadingVideo}
                          className="ghost-blue-btn px-4 py-2 rounded-lg flex items-center gap-2 text-sm flex-1 disabled:opacity-50"
                          data-testid="upload-video-btn"
                        >
                          {uploadingVideo ? <><span className="w-4 h-4 border-2 border-[#00d4ff]/30 border-t-[#00d4ff] rounded-full animate-spin" />Uploader...</> : <><Video size={16} />Tilføj video</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-3xl font-['Oswald'] font-bold text-white mb-2">
                        {selectedProduct.title}
                      </h2>
                      {selectedProduct.category && (
                        <span className="text-sm text-gray-500 uppercase tracking-wider">
                          {selectedProduct.category}
                        </span>
                      )}
                    </div>
                    
                    {/* Admin actions */}
                    {isAdmin && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            openEditForm(selectedProduct);
                            setSelectedProduct(null);
                          }}
                          className="ghost-blue-btn p-2 rounded-lg"
                          title="Rediger"
                        >
                          <Edit size={20} />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(selectedProduct.product_id)}
                          className="bg-red-600/20 hover:bg-red-600/40 text-red-400 p-2 rounded-lg"
                          title="Slet"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="text-3xl font-bold text-[var(--neon-blue)] mb-6">
                    {formatPrice(selectedProduct.price)}
                  </div>

                  {/* Sizes */}
                  {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-400 mb-3">Størrelser</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct.sizes.map((sizeObj) => (
                          <div
                            key={sizeObj.size}
                            className="px-4 py-2 rounded-lg bg-neutral-800 border border-neutral-700 text-white"
                          >
                            {sizeObj.size}
                            {sizeObj.stock !== undefined && (
                              <span className="text-gray-500 ml-2 text-sm">
                                ({sizeObj.stock} stk)
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Beskrivelse</h4>
                    <p className="text-gray-300 whitespace-pre-line">
                      {selectedProduct.description}
                    </p>
                  </div>

                  {/* Contact info */}
                  <div className="mt-8 p-4 rounded-lg bg-neutral-900 border border-neutral-800">
                    <p className="text-gray-400 text-sm">
                      Interesseret? Kontakt os for at bestille eller spørg i klubhuset.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Product Modal */}
      {showCreateForm && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={resetForm}
        >
          <div 
            className="mc-card max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-['Oswald'] font-bold text-white">
                  {editingProduct ? "Rediger produkt" : "Nyt produkt"}
                </h2>
                <button onClick={resetForm} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateProduct} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Titel *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="mc-input w-full"
                    required
                    placeholder="F.eks. The Others MC T-shirt"
                  />
                </div>

                {/* Short Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Kort beskrivelse
                  </label>
                  <input
                    type="text"
                    value={formData.short_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                    className="mc-input w-full"
                    placeholder="Vises i produktoversigten"
                    maxLength={150}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Fuld beskrivelse *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="mc-input w-full h-32 resize-none"
                    required
                    placeholder="Detaljeret produktbeskrivelse..."
                  />
                </div>

                {/* Price & Category */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Pris (DKK) *
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      className="mc-input w-full"
                      required
                      min="0"
                      step="0.01"
                      placeholder="299"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Kategori
                    </label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="mc-input w-full"
                      placeholder="F.eks. Tøj, Tilbehør"
                    />
                  </div>
                </div>

                {/* Sizes */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Størrelser (valgfrit - klik for at tilføje)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {sizePresets.map((size) => {
                      const isSelected = formData.sizes.some(s => s.size === size);
                      return (
                        <button
                          key={size}
                          type="button"
                          onClick={() => toggleSize(size)}
                          className={`px-4 py-2 rounded-lg border transition-all ${
                            isSelected 
                              ? 'bg-[var(--neon-blue)] border-[var(--neon-blue)] text-black font-bold' 
                              : 'bg-neutral-800 border-neutral-700 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Active status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-5 h-5 rounded border-neutral-600 bg-neutral-800 text-[var(--neon-blue)] focus:ring-[var(--neon-blue)]"
                  />
                  <label htmlFor="is_active" className="text-gray-300">
                    Produkt er aktivt (synligt i webshoppen)
                  </label>
                </div>

                {/* Submit buttons */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={savingProduct || savedProduct}
                    className="radial-btn px-8 py-3 rounded-lg flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="save-product-btn"
                  >
                    {savingProduct ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Gemmer...</> : savedProduct ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Gemt!</> : editingProduct ? "Gem ændringer" : "Opret produkt"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="ghost-blue-btn px-6 py-3 rounded-lg"
                  >
                    Annuller
                  </button>
                </div>

                {editingProduct && (
                  <p className="text-gray-500 text-sm text-center">
                    Tip: Billeder og videoer tilføjes efter produktet er oprettet
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebshopPage;
